import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Shield,
  DollarSign,
  CheckCircle,
  Users,
  Building2,
  Stethoscope,
  Pill,
  Brain,
  Hospital,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  AlertTriangle,
  TrendingDown,
  Briefcase,
  Star,
  BadgeCheck,
  Activity,
} from "lucide-react";

/* ── Animated counter ── */
const AnimatedCounter = ({ end, prefix = "", suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = Date.now();
          const numEnd = parseFloat(String(end).replace(/[^0-9.]/g, ""));
          const tick = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numEnd));
            if (progress < 1) requestAnimationFrame(tick);
            else setCount(numEnd);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ── FAQ Item ── */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed whitespace-pre-line">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const coverageItems = [
  { icon: Stethoscope, label: "Doctor Visits" },
  { icon: Hospital, label: "Hospital Care" },
  { icon: Brain, label: "Mental Health" },
  { icon: Pill, label: "Prescription Drugs" },
  { icon: Activity, label: "Preventive Care" },
  { icon: Heart, label: "Emergency Services" },
];

const faqs = [
  {
    question: "Will my taxes go up?",
    answer:
      "For most families, no. The contributions to eNVy Healthcare replace what you and your employer already pay in premiums, deductibles, and co-pays. For the majority of Nevadans, total healthcare costs will go down — not up. The system redirects money already being spent, it doesn't create new costs.",
  },
  {
    question: "Can I keep my private insurance?",
    answer:
      "Yes. eNVy Healthcare is an option, not a mandate. If you prefer your current plan, you can keep it. But eNVy Healthcare is designed to be better and cheaper — so most people and employers will choose it voluntarily.",
  },
  {
    question: "Is this legal? Can a state actually do this?",
    answer:
      "Yes. States have the authority to create their own healthcare programs. We will use federal waivers — including Medicaid waivers and ACA Section 1332 waivers — to redirect federal funds into the state system. Multiple states have explored or implemented similar approaches.",
  },
  {
    question: "Will doctors accept it?",
    answer:
      "Yes — and they'll prefer it. eNVy Healthcare pays providers 140-160% of Medicare rates, which is more than many insurance plans pay today. Plus, doctors get faster payments, less paperwork, and fewer unpaid bills. We are not asking providers to take less — we are offering a better system.",
  },
  {
    question: "What happens if it costs more than expected?",
    answer:
      "The plan includes multiple safeguards: a reserve fund for high-cost cases, independent financial audits, contribution caps tied to inflation, and a phased rollout that allows for adjustment before full-scale expansion. This is built to last.",
  },
  {
    question: "Can people move here just to use it?",
    answer:
      "Full benefits require Nevada residency. There will be a residency requirement to prevent people from moving to the state solely to access the system, ensuring it remains sustainable for Nevadans.",
  },
];

const HealthcarePage = () => {
  return (
    <div data-testid="healthcare-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mb-4">
            <span className="text-sm font-bold tracking-[0.2em] text-[#D4AF37]">
              eNVy Healthcare
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-3xl">
            A Healthcare System That Actually Works for Nevada
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-6">
            Lower costs. No denied care. Real coverage you can rely on.
          </p>

          {/* Name explanation */}
          <div className="inline-flex items-center gap-3 bg-white/10 border border-white/20 px-5 py-3 mb-8">
            <Star className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
            <p className="text-white/90 text-sm">
              Why <strong className="text-[#D4AF37]">eNVy</strong>? Because{" "}
              <strong>NV</strong> stands for Nevada — and this plan will make
              Nevada's healthcare the <em>envy</em> of the entire country.
            </p>
          </div>

          {/* 3 key bullets */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              "No more $20,000+ family healthcare costs",
              "No more insurance denials for necessary care",
              "A simple, transparent system that saves money",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-white/90 text-sm font-medium">
                  {item}
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              See How It Works
            </a>
            <a
              href="#full-plan"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              View Full Plan
            </a>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center">
            The Current System Is Failing Nevada Families
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* What You Pay */}
            <div className="bg-red-50 border border-red-200 p-8">
              <h3 className="text-lg font-bold text-red-800 mb-6 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                What You Pay
              </h3>
              <div className="space-y-4">
                <div className="border-b border-red-200 pb-4">
                  <p className="text-3xl font-black text-red-600">
                    $8K–$10K
                  </p>
                  <p className="text-sm text-red-700">
                    Average individual healthcare cost per year
                  </p>
                </div>
                <div className="border-b border-red-200 pb-4">
                  <p className="text-3xl font-black text-red-600">
                    $20K–$30K
                  </p>
                  <p className="text-sm text-red-700">
                    Average family healthcare cost per year
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-black text-red-600">Rising</p>
                  <p className="text-sm text-red-700">
                    Costs increase every year with no end in sight
                  </p>
                </div>
              </div>
            </div>

            {/* What You Get */}
            <div className="bg-slate-100 border border-slate-200 p-8">
              <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-slate-500" />
                What You Get
              </h3>
              <div className="space-y-4">
                {[
                  {
                    label: "Denied Claims",
                    desc: "Insurance companies routinely deny care your doctor says you need",
                  },
                  {
                    label: "Medical Debt",
                    desc: "One hospital visit can lead to financial ruin for working families",
                  },
                  {
                    label: "Delayed Care",
                    desc: "People skip or delay treatment because they can't afford it",
                  },
                  {
                    label: "Confusion",
                    desc: "Deductibles, co-pays, networks, prior auth — designed to be confusing",
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 border-b border-slate-200 pb-4 last:border-0 last:pb-0"
                  >
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-slate-800 text-sm">
                        {item.label}
                      </p>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE SOLUTION ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Introducing eNVy Healthcare
            </h2>
            <p className="text-slate-600 leading-relaxed">
              A state-run healthcare option that works alongside private
              insurance — but is designed to be better. Optional, not mandatory.
              Covers all essential care. No denial of doctor-approved treatment.
              Lower total cost.
            </p>
          </div>

          {/* Coverage grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {coverageItems.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="bg-white border border-slate-200 p-5 text-center hover:border-[#D4AF37] transition-colors"
                >
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-[#002B7F] text-white mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-bold text-slate-900">
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Highlight box */}
          <div className="bg-[#002B7F] p-6 text-center max-w-2xl mx-auto">
            <p className="text-white font-bold text-lg">
              No unnecessary denials. If your doctor says you need care, you get
              it.
            </p>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Here's Exactly How eNVy Healthcare Works
            </h2>
          </div>

          {/* 4.1 Contributions Table */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6">
              Contributions
            </h3>
            <div className="border border-slate-200 overflow-hidden mb-6">
              <div className="grid grid-cols-3 bg-[#002B7F] text-white">
                <div className="p-4 font-bold text-sm border-r border-white/20">
                  Who Pays
                </div>
                <div className="p-4 font-bold text-sm border-r border-white/20">
                  Amount
                </div>
                <div className="p-4 font-bold text-sm">
                  Why It's Lower
                </div>
              </div>
              {[
                [
                  "Employers",
                  "6–7% of payroll",
                  "Less than current insurance premiums",
                ],
                [
                  "Employees",
                  "2–4% of income",
                  "Predictable + capped. No surprise bills",
                ],
                [
                  "Minimum contribution",
                  "~$50 / $100 per month",
                  "Ensures system stability",
                ],
              ].map((row, i) => (
                <div
                  key={i}
                  className="grid grid-cols-3 border-b border-slate-200 last:border-0"
                >
                  <div className="p-4 font-medium text-slate-900 text-sm border-r border-slate-200">
                    {row[0]}
                  </div>
                  <div className="p-4 text-[#002B7F] font-bold text-sm border-r border-slate-200">
                    {row[1]}
                  </div>
                  <div className="p-4 text-slate-600 text-sm">{row[2]}</div>
                </div>
              ))}
            </div>

            <div className="bg-emerald-50 border border-emerald-200 p-5 inline-flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-emerald-800 text-sm font-medium">
                Low-income residents pay reduced or zero contributions.
              </p>
            </div>
          </div>

          {/* 4.2 Where the Money Comes From */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6">
              Where the Money Comes From
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {[
                {
                  title: "Existing Employer Spending",
                  desc: "Healthcare dollars already being spent by employers — redirected into a more efficient system",
                },
                {
                  title: "State Healthcare Funds",
                  desc: "Reorganized and consolidated for maximum impact",
                },
                {
                  title: "Federal Funding",
                  desc: "Medicaid waivers and ACA Section 1332 waivers redirect federal dollars to the state system",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-[#F8F9FA] border border-slate-200 p-6"
                >
                  <h4 className="font-bold text-slate-900 mb-2 text-sm">
                    {item.title}
                  </h4>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-5">
              <p className="text-[#002B7F] font-bold text-sm">
                This plan uses money already being spent — it just uses it more
                efficiently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WILL DOCTORS ACCEPT IT? ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Will Doctors Accept It? Yes — And Here's Why
              </h2>
              <div className="space-y-4">
                {[
                  {
                    icon: DollarSign,
                    title: "Pays 140–160% of Medicare",
                    desc: "More than many private insurance plans pay today",
                  },
                  {
                    icon: Clock,
                    title: "Faster Payments",
                    desc: "Streamlined processing means doctors get paid quicker",
                  },
                  {
                    icon: FileText,
                    title: "Less Paperwork",
                    desc: "One system, one set of rules — no more juggling dozens of insurers",
                  },
                  {
                    icon: Shield,
                    title: "Fewer Unpaid Bills",
                    desc: "Universal coverage means fewer patients who can't pay at all",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-4 bg-white/10 p-4"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37] text-black flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-white text-sm">
                          {item.title}
                        </p>
                        <p className="text-white/70 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white/10 border border-white/20 p-8">
              <p className="text-white/90 leading-relaxed mb-4">
                We reference how Medicare and programs in states like Maryland
                already demonstrate that strong reimbursement rates attract
                providers and improve care quality.
              </p>
              <p className="text-[#D4AF37] font-bold text-lg">
                We are not asking providers to take less — we are offering a
                better system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRESCRIPTION DRUGS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Ending Price Gouging on Life-Saving Drugs
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                step: "1",
                title: "Negotiate at Scale",
                desc: "Use the full purchasing power of Nevada's population to negotiate directly with drug manufacturers for lower prices.",
              },
              {
                step: "2",
                title: "Bulk Purchasing",
                desc: "Pool demand across the state to secure volume discounts, passing savings directly to patients.",
              },
              {
                step: "3",
                title: "State Manufacturing Authority",
                desc: "Establish the legal framework for Nevada to produce or import essential medications if companies refuse fair pricing.",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="bg-[#F8F9FA] border border-slate-200 p-6"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F] p-6 text-center max-w-3xl mx-auto">
            <p className="text-white font-bold">
              If drug companies refuse fair pricing, Nevada will act using every
              legal tool available to ensure access to affordable medication.
            </p>
          </div>
        </div>
      </section>

      {/* ── IMPLEMENTATION TIMELINE ── */}
      <section id="full-plan" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              A Responsible, Step-by-Step Rollout
            </h2>
            <p className="text-slate-600">
              This prevents disruption and protects hospitals and patients.
            </p>
          </div>

          <div className="space-y-6">
            {/* Phase 1 */}
            <div className="grid md:grid-cols-[140px_1fr] gap-6">
              <div className="bg-[#002B7F] p-4 flex items-center justify-center">
                <p className="text-white font-black text-lg">Year 1–2</p>
              </div>
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-3">
                  Build & Prepare
                </h3>
                <ul className="space-y-2">
                  {[
                    "Build system infrastructure and technology",
                    "Secure federal Medicaid and ACA Section 1332 waivers",
                    "Begin small business enrollment",
                    "Establish provider network and payment systems",
                  ].map((item, i) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Phase 2 */}
            <div className="grid md:grid-cols-[140px_1fr] gap-6">
              <div className="bg-[#D4AF37] p-4 flex items-center justify-center">
                <p className="text-black font-black text-lg">Year 3–5</p>
              </div>
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-3">
                  Scale & Expand
                </h3>
                <ul className="space-y-2">
                  {[
                    "Expand statewide availability",
                    "Transition larger employers into the system",
                    "Full prescription drug negotiation program active",
                    "Veterans integration complete",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SAFEGUARDS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Built to Last
              </h2>
              <div className="space-y-4">
                {[
                  {
                    icon: Shield,
                    title: "Reserve Fund",
                    desc: "Dedicated fund for high-cost cases and unexpected surges",
                  },
                  {
                    icon: FileText,
                    title: "Independent Audits",
                    desc: "Regular independent financial audits ensure accountability",
                  },
                  {
                    icon: TrendingDown,
                    title: "Contribution Caps",
                    desc: "Tied to inflation — costs can't spiral out of control",
                  },
                  {
                    icon: BadgeCheck,
                    title: "Residency Requirement",
                    desc: "Full benefits require Nevada residency to ensure sustainability",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-4 p-4 bg-[#F8F9FA] border border-slate-200"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-sm">
                          {item.title}
                        </p>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Veterans */}
            <div className="bg-[#002B7F] p-8">
              <h3 className="text-xl font-bold text-white mb-6">
                Expanding Care for Nevada Veterans
              </h3>
              <p className="text-white/80 mb-6">
                Our veterans deserve more options, not fewer. eNVy Healthcare
                expands what's available:
              </p>
              <ul className="space-y-3">
                {[
                  "Federal waivers to integrate veteran care",
                  "Optional use alongside existing VA benefits",
                  "More provider access throughout Nevada",
                  "Faster care — no more months-long waits",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/90 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── ECONOMIC IMPACT ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Lower Healthcare Costs = More Jobs
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              When healthcare costs go down, businesses grow, wages increase, and
              workers are free to pursue better opportunities.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Building2,
                title: "Lower Employer Costs",
                desc: "Businesses save on healthcare, freeing capital for growth and wages",
              },
              {
                icon: Users,
                title: "Attract New Businesses",
                desc: "Nevada becomes a magnet for companies seeking lower operating costs",
              },
              {
                icon: Briefcase,
                title: 'Reduce "Job Lock"',
                desc: "Workers aren't trapped in jobs just for insurance — they can start businesses, change careers",
              },
              {
                icon: DollarSign,
                title: "Increase Wage Flexibility",
                desc: "Money saved on premiums can go toward higher salaries",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white border border-slate-200 p-6 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 text-sm">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── STRONG CLOSE ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            A Healthcare System That Works for You
          </h2>
          <div className="space-y-4 mb-8">
            {[
              "No more choosing between rent and medicine",
              "No more fear of medical bills",
              "Real coverage, real security",
            ].map((item) => (
              <p
                key={item}
                className="text-lg text-slate-600 font-medium"
              >
                {item}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Healthcare That Works — For Everyone
          </h2>
          <p className="text-white/70 mb-8">
            eNVy Healthcare: making Nevada the envy of the country.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Join the Campaign
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/bail-reform"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Pretrial Justice Reform <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthcarePage;
